import { useMemo, useState } from "react";
import TokenInput from "./TokenInput";
import Toggle from "../common/Toggle";
import { resolveCountry, Flag } from "./lawyerFlag";
import { CheckmarkIcon } from "./v2/icons";

const asList = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

// Normalize a raw profile payload into the editable form shape. Defaults for
// the privacy toggles follow product rule 4 (email ON, phone OFF) when the
// backend has not sent a value yet.
// NOTE (Marin decision 2026-07-23): the phone-visibility TOGGLE was removed
// from the UI (phone is gone from all lawyer surfaces), but the stored
// `contact_phone_public` value keeps round-tripping through toForm → save
// UNCHANGED — it is never forced to false and stays in the PATCH whitelist.
const toForm = (p = {}) => ({
  specializations: asList(p.specializations).length
    ? asList(p.specializations)
    : p.specialization
    ? [p.specialization]
    : [],
  jurisdictions: asList(p.jurisdictions),
  languages: asList(p.languages),
  firm: p.firm || "",
  bio: p.bio || "",
  city: p.city || "",
  country: p.country || "",
  license_number: p.license_number || "",
  website_url: p.website_url || "",
  linkedin_url: p.linkedin_url || "",
  contact_email_public:
    p.contact_email_public === undefined ? true : !!p.contact_email_public,
  contact_phone_public:
    p.contact_phone_public === undefined ? false : !!p.contact_phone_public,
});

// Field label per artboards 1i/1j: 12px/600 #344054 with an optional suffix —
// "· missing" in #E04F16 600 (incomplete completeness fields) or a muted
// "· needed for verification" / "· optional" in #98A2B3 400. Filled fields
// carry no suffix (1j).
const FieldLabel = ({ text, suffix, suffixTone }) => (
  <label className="block text-[12px] font-semibold text-[#344054] mb-[5px]">
    {text}
    {suffix && (
      <span
        className={
          suffixTone === "missing"
            ? "text-[#E04F16] font-semibold"
            : "text-[#98A2B3] font-normal"
        }
      >
        {" · "}
        {suffix}
      </span>
    )}
  </label>
);

// Artboard input: border #DDE3EC, radius 9px, padding 9px 12px, 13px text.
// Filled values render font-weight 500 (1j); focus affordance kept from the
// existing build (accessibility, not encoded in the static handoff).
const inputCls =
  "w-full border border-[#DDE3EC] rounded-[9px] px-[12px] py-[9px] text-[13px] text-[#101828] placeholder:text-[#98A2B3] bg-white focus:outline-none focus:border-[#2283FF] focus:ring-2 focus:ring-[#2283FF]/20";

const LawyerProfileForm = ({
  initialProfile,
  onSave,
  saving = false,
  countrySuggestions = [],
  contactEmail = "",
  onPreview = null,
  t = (s) => s,
}) => {
  const [form, setForm] = useState(() => toForm(initialProfile));

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Verification is admin/server-side only (read-only on the profile GET);
  // drives the 1j license verified tick — real state, never decorative.
  const isVerified = initialProfile?.is_verified === true;

  // Completeness across the directory-relevant fields (headquarters = city +
  // country counts as one). Drives the nudge + progress bar.
  const completion = useMemo(() => {
    const checks = [
      { key: "specializations", ok: form.specializations.length > 0, label: t("Specializations") },
      { key: "jurisdictions", ok: form.jurisdictions.length > 0, label: t("Jurisdictions") },
      { key: "languages", ok: form.languages.length > 0, label: t("Languages") },
      { key: "firm", ok: !!form.firm.trim(), label: t("Firm") },
      { key: "bio", ok: !!form.bio.trim(), label: t("Bio") },
      { key: "headquarters", ok: !!(form.city.trim() && form.country.trim()), label: t("Headquarters") },
      { key: "license_number", ok: !!form.license_number.trim(), label: t("License number") },
    ];
    const done = checks.filter((c) => c.ok).length;
    return {
      percent: Math.round((done / checks.length) * 100),
      missing: checks.filter((c) => !c.ok),
      complete: done === checks.length,
    };
  }, [form, t]);

  const missingKeys = useMemo(
    () => new Set(completion.missing.map((m) => m.key)),
    [completion]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const hqFlag = resolveCountry(form.country.trim());
  const showLicenseTick = isVerified && !!form.license_number.trim();

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
      {/* Page header — title + state subtitle + status pill (1i amber / 1j green) */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[19px] font-bold text-[#101828]">
            {t("Lawyer profile")}
          </h1>
          <p className="text-[13px] text-[#667085] mt-[3px]">
            {completion.complete
              ? t("This card is shown in the Lawyers directory.")
              : t(
                  "This card is shown in the Lawyers directory. It was created when you added the lawyer role."
                )}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-[6px] px-[11px] py-[5px] rounded-full text-[12px] font-semibold flex-none mt-[2px] border ${
            completion.complete
              ? "bg-[#E7F8EF] border-[#B7E7CC] text-[#0E7A45]"
              : "bg-[#FFF6E8] border-[#F5DDAE] text-[#B25E09]"
          }`}
        >
          <span aria-hidden="true">●</span>
          {completion.complete ? t("Live in directory") : t("Incomplete")}
        </span>
      </div>

      {/* Completion nudge banner — only while incomplete (1i); 1j has none.
          Percent comes from the real 7-check meter, never the mock's 30%. */}
      {!completion.complete && (
        <div className="bg-[#EBF3FF] border border-[#C9E1FF] rounded-[12px] px-[16px] py-[14px]">
          <div className="flex justify-between text-[13px] font-semibold text-[#1567D2] mb-2">
            <span>
              {t("Your lawyer card is")} {completion.percent}% {t("complete")}
            </span>
            <span>{completion.percent}%</span>
          </div>
          <div className="h-[6px] rounded-full bg-[#D3E5FD]">
            <div
              className="h-[6px] rounded-full bg-[#2283FF] transition-all"
              style={{ width: `${completion.percent}%` }}
            />
          </div>
          <p className="text-[12px] text-[#3D6FB4] mt-2">
            {t(
              "Complete it to appear stronger in the directory. Cards with jurisdictions and specializations get contacted first."
            )}
          </p>
        </div>
      )}

      {/* Fields grid (2-col, 1i/1j) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-[14px] gap-x-[18px]">
        <div>
          <FieldLabel
            text={t("Specializations")}
            suffix={missingKeys.has("specializations") ? t("missing") : null}
            suffixTone="missing"
          />
          <TokenInput
            value={form.specializations}
            onChange={(v) => set("specializations", v)}
            placeholder={t("e.g. Transfer Law, Contract Law")}
            addLabel={t("Add specializations")}
          />
        </div>

        <div>
          <FieldLabel
            text={t("Jurisdictions / countries of practice")}
            suffix={missingKeys.has("jurisdictions") ? t("missing") : null}
            suffixTone="missing"
          />
          <TokenInput
            value={form.jurisdictions}
            onChange={(v) => set("jurisdictions", v)}
            suggestions={countrySuggestions}
            placeholder={t("Countries of practice")}
            addLabel={t("Add countries")}
            withFlags
          />
        </div>

        <div>
          <FieldLabel
            text={t("Headquarters")}
            suffix={missingKeys.has("headquarters") ? t("missing") : null}
            suffixTone="missing"
          />
          <div className="flex gap-2">
            <input
              className={`flex-1 min-w-0 ${inputCls} ${form.city ? "font-medium" : ""}`}
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
              placeholder={t("City")}
              aria-label={t("City")}
            />
            {/* Country stays FREE-ENTRY per existing logic (Rule 10) — flag
                when resolvable, rest of the artboard styling kept. The
                decorative chevron was removed (Marin decision A16 option b,
                2026-07-23): it opened nothing and read as a fake dropdown. */}
            <div className="relative flex-1 min-w-0">
              {form.country.trim() && hqFlag.code && (
                <span className="absolute left-[12px] top-1/2 -translate-y-1/2 flex">
                  <Flag code={hqFlag.code} width={16} height={11.5} label={form.country} />
                </span>
              )}
              <input
                className={`${inputCls} ${
                  form.country.trim() && hqFlag.code ? "pl-[34px]" : ""
                } ${form.country ? "font-medium" : ""}`}
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
                placeholder={t("Country")}
                aria-label={t("Country")}
              />
            </div>
          </div>
        </div>

        <div>
          <FieldLabel
            text={t("Languages")}
            suffix={missingKeys.has("languages") ? t("missing") : null}
            suffixTone="missing"
          />
          <TokenInput
            value={form.languages}
            onChange={(v) => set("languages", v)}
            placeholder={t("e.g. English, Spanish")}
            addLabel={t("Add languages")}
          />
        </div>

        <div>
          <FieldLabel
            text={t("License number")}
            suffix={
              missingKeys.has("license_number")
                ? t("needed for verification")
                : null
            }
            suffixTone="muted"
          />
          <div className="relative">
            <input
              className={`${inputCls} ${showLicenseTick ? "pr-[34px]" : ""} ${
                form.license_number ? "font-medium" : ""
              }`}
              value={form.license_number}
              onChange={(e) => set("license_number", e.target.value)}
              placeholder={t("e.g. ICAM 84 291")}
            />
            {showLicenseTick && (
              <span
                className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[#12B76A] flex"
                data-testid="license-verified-tick"
              >
                <CheckmarkIcon size={13} strokeWidth={2.5} />
              </span>
            )}
          </div>
        </div>

        <div>
          <FieldLabel
            text={t("Firm / organization")}
            suffix={form.firm.trim() ? null : t("optional")}
            suffixTone="muted"
          />
          <input
            className={`${inputCls} ${form.firm ? "font-medium" : ""}`}
            value={form.firm}
            onChange={(e) => set("firm", e.target.value)}
            placeholder={t("Firm name")}
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel
            text={t("Short bio")}
            suffix={form.bio.trim() ? null : t("optional")}
            suffixTone="muted"
          />
          <textarea
            className={`${inputCls} py-[10px] h-[56px] min-h-[56px] leading-[1.5] resize-y`}
            value={form.bio}
            onChange={(e) => set("bio", e.target.value)}
            placeholder={t(
              "A few lines on your practice: experience, notable matters, who you work with."
            )}
          />
        </div>
      </div>

      {/* Contact visibility card (1i/1j) */}
      <div className="bg-white border border-[#E6EAF2] rounded-[12px] px-[18px] py-[16px]">
        <h3 className="text-[13px] font-bold text-[#101828] mb-[2px]">
          {t("Contact visibility")}
        </h3>
        <p className="text-[12px] text-[#667085] mb-[14px]">
          {t(
            "In-platform chat is always available to clubs and agencies. These only control what's shown on your card."
          )}
        </p>
        {/* Email visibility only — the phone toggle was removed entirely
            (Marin decision 2026-07-23, phone gone from all lawyer surfaces).
            The stored contact_phone_public value still round-trips via toForm
            → save, untouched. */}
        <div className="flex flex-col gap-[12px]">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-[#344054]">
                {t("Show email on my card")}
              </p>
              <p className="text-[11.5px] text-[#98A2B3] truncate">
                {contactEmail || t("Others can see your email in the directory")}
              </p>
            </div>
            <Toggle
              sm
              checked={form.contact_email_public}
              onChange={(v) => set("contact_email_public", v)}
              label={t("Show email on my card")}
            />
          </div>
        </div>
      </div>

      {/* Footer actions (1i/1j): "Preview my card" link left (mr-auto), Save
          right. The preview link was a FUNCTION-TODO row (no logic existed);
          wired by follow-up A3 (FOLLOWUP-PACK-BRIEF, C27): the form only
          reports its LIVE state up via onPreview — the parent owns the
          read-only preview surface. Renders only when the parent wires it. */}
      <div className="flex justify-end gap-[10px] items-center">
        {onPreview && (
          <button
            type="button"
            onClick={() => onPreview(form)}
            className="mr-auto text-[12.5px] text-[#2283FF] font-semibold hover:underline cursor-pointer"
          >
            {t("Preview my card")}
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="bg-[#2283FF] hover:bg-[#1570E8] text-white rounded-[9px] px-[18px] py-[9px] text-[13px] font-semibold disabled:opacity-60"
        >
          {saving ? t("Saving...") : t("Save changes")}
        </button>
      </div>
    </form>
  );
};

export default LawyerProfileForm;
